import { useCallback, useEffect, useState } from "react";
import { container } from "tsyringe";
import DefaultCastImage from "assets/images/default-avatar.svg";
import LazyImage from "components/elements/LazyImage";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import { useProjectTeamContext } from "components/pages/ProfessionalSpace/contexts/ProjectTeam";
import ManageCardMenu from "components/pages/ProfessionalSpace/components/ManageCardMenu";
import ProjectTeamCastResponseResource from "common/resources/Project/Config/Team/Cast/ProjectTeamCastResponseResource";
import { useProjectId } from "hooks/useProjectId";
import CastService from "services/projects/CastService";
import { translateISO369 } from "utils/I18nText";
import { formatContributorName } from "utils/UserUtils";
import classes from "./classes.module.scss";

type IProps = {
	cast: ProjectTeamCastResponseResource;
	onEditClick: () => void;
	onDeleteClick: () => void;
};

const castService = container.resolve(CastService);

export default function CastingCard(props: IProps) {
	const { imageUrl } = useCastingInfo(props.cast);

	return (
		<div className={classes["root"]}>
			<div className={classes["menu-container"]}>
				<ManageCardMenu onEditClick={props.onEditClick} onDeleteClick={props.onDeleteClick} className={classes["card-menu"]} />
			</div>

			<LazyImage src={imageUrl} alt={translateISO369(props.cast.characterName) ?? ""} className={classes["image"]} />

			<div className={classes["info-container"]}>
				<Typography type="p" size="xlarge" weight="bold">
					{translateISO369(props.cast.characterName)}
				</Typography>

				<div className={classes["info"]}>
					<Typography type="p" size="large" weight="regular">
						{I18n.asset.pages.professional_space.tabs.casting.casting_card.played_by}
					</Typography>
					<Typography type="p" size="large" weight="bold" color="secondary">
						{formatContributorName(props.cast.actor)}
					</Typography>
				</div>
			</div>
		</div>
	);
}

export const useCastingInfo = (cast: Pick<ProjectTeamCastResponseResource, "id" | "image">) => {
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();
	const [imageUrl, setImageUrl] = useState<string>(DefaultCastImage);
	const [imageFile, setImageFile] = useState<File | null>(null);

	const clearImage = useCallback(() => {
		setImageUrl(DefaultCastImage);
		setImageFile(null);
	}, []);

	const fetchImage = useCallback(() => {
		const { image } = cast;

		if (!image) return clearImage();

		castService.downloadImage(projectId, projectTeamId, cast.id).then((blob) => {
			if (!blob) return clearImage();

			setImageUrl(URL.createObjectURL(blob));
			setImageFile(
				new File([blob], image.name ?? "Avatar", {
					type: blob?.type || image.mimeType,
				}),
			);
		});
	}, [cast, projectId, projectTeamId, clearImage]);

	useEffect(() => {
		fetchImage();
	}, [fetchImage]);

	return { imageUrl, imageFile };
};
